import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Backdrop from "Components/Common/Backdrop";
import { getOptionValue } from "common/enums";

import getFileExtension from "utils/getFileExtension";
import removeExtension from "utils/removeExtension";
import { getImageUrl } from "utils";

import {
  useGetMenuDocumentParentQuery,
  useLazyGetMenuDetailsById,
  usePostMenuItemsMutation,
} from "apis/HomePage";

import pdfImg from "assets/images/pdf.png";

const validFileFormats = ["pdf", "PDF"];

const ADD_NEW_DOCUMENT = "Add New Document";

const validationSchema = Yup.object({
  selectedContentType: Yup.string().test(
    "isValidDocumentType",
    "",
    function () {
      const [{ value: current = null } = {}] = this.from || [];
      if (!current.isAddingNewCategory && !current.selectedContentType) {
        return this.createError({
          message: "Document type is required",
        });
      }
      return true;
    }
  ),
  selectParentDocument: Yup.string().required("Category is required"),
  documentName: Yup.string().test("isValidDocumentName", "", function () {
    const [{ value: current = null } = {}] = this.from || [];
    if (!current.isAddingNewCategory && !current.documentName) {
      return this.createError({
        message: "Document name is required",
      });
    }
    return true;
  }),
  categoryName: Yup.string().test("isValidCategoryName", "", function () {
    const [{ value: current = null } = {}] = this.from || [];
    if (current.isAddingNewCategory && !current.categoryName) {
      return this.createError({
        message: "Category name is required",
      });
    }
    return true;
  }),
});

const DocumentModal = ({ isOpen, handleClose, menuData: menuOptions, t }) => {
  const {
    data: { data: categoriesOptions } = {},
    isLoading: isCategoriesOptionsLoading,
  } = useGetMenuDocumentParentQuery();

  const [
    getMenuDetailsById,
    {
      data: { data: { id, name, content, parent } = {} } = {},
      isLoading: isMenuDetailsLoading,
    },
  ] = useLazyGetMenuDetailsById();

  const [updatePageContent, { isLoading }] = usePostMenuItemsMutation();

  const [documentToPreview, setDocumentToPreview] = useState();

  const initialValues = useMemo(
    () => ({
      selectedContentType: id ?? "",
      selectParentDocument: parent?.id ?? "",
      isAddingNewDocument: false,
      isAddingNewCategory: false,
      documentName: name ?? "",
      document: "",
      categoryName: "",
    }),
    [parent, name]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async ({
      isAddingNewDocument,
      isAddingNewCategory,
      document,
      selectParentDocument,
      documentName,
      categoryName,
    }) => {
      if (isAddingNewDocument && !document) {
        toast.error("Document is Required");
      } else {
        try {
          const formData = new FormData();

          if (!isAddingNewCategory) {
            if (document) {
              formData.append("document", document);
            } else {
              formData.append("content", content);
            }
            formData.append(
              "contentType",
              getOptionValue("homeContentType", "Document")
            );
          }

          formData.append(
            "id",
            isAddingNewDocument || isAddingNewCategory ? 0 : id
          );
          formData.append("isHeader", true);
          formData.append(
            "name",
            isAddingNewCategory ? categoryName : documentName
          );

          formData.append("parentId", selectParentDocument);
          await updatePageContent(formData);
          handleClose();
        } catch (error) {}
      }
    },
  });

  const {
    values: {
      selectedContentType,
      isAddingNewDocument,
      isAddingNewCategory,
      document,
    },
    handleSubmit,
    setFieldValue,
  } = formik;

  useEffect(() => {
    (async () => {
      try {
        if (selectedContentType && selectedContentType !== ADD_NEW_DOCUMENT) {
          await getMenuDetailsById(selectedContentType);
        }
        if (selectedContentType === ADD_NEW_DOCUMENT) {
          setFieldValue("isAddingNewDocument", true);
        } else {
          setFieldValue("isAddingNewDocument", false);
        }
      } catch (error) {}
    })();
  }, [selectedContentType]);

  const updateDocumentOptions = useMemo(
    () =>
      menuOptions?.length
        ? [{ name: ADD_NEW_DOCUMENT, id: ADD_NEW_DOCUMENT }, ...menuOptions]
        : [{ name: ADD_NEW_DOCUMENT, id: ADD_NEW_DOCUMENT }],
    [menuOptions]
  );

  const fileChangeHandler = (event) => {
    try {
      const fileExtension = getFileExtension(
        event.target.files[0]?.name
      ).toLowerCase();
      if (!validFileFormats.includes(fileExtension)) {
        toast.error("Invalid file format, Please upload pdf");
      } else {
        setFieldValue("document", event.target.files[0]);
      }
      event.target.value = "";
    } catch (err) {}
  };

  const fileClickHandler = () => {
    setDocumentToPreview({
      documentTypeName: name,
      url: getImageUrl(content),
      documentType: "pdf",
      documentName: removeExtension(name ?? ""),
    });
  };

  const newDocumentFileHandler = () => {
    const fileExtension = getFileExtension(document.name ?? "");
    setDocumentToPreview({
      documentTypeName: document.name,
      url: URL.createObjectURL(document),
      documentType: fileExtension,
      documentName: removeExtension(document.name ?? ""),
    });
  };

  if (isCategoriesOptionsLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      <Backdrop open={isMenuDetailsLoading} />
      <Modal
        handleClose={handleClose}
        isOpen={isOpen}
        maxWidth="md"
        title="Update Document"
      >
        <Grid container columnSpacing={1} rowSpacing={1} className="mt-2">
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!isAddingNewCategory}
                  onChange={() => {
                    setFieldValue("isAddingNewCategory", !isAddingNewCategory);
                    if (!isAddingNewCategory) {
                      setFieldValue("selectedContentType", "");
                      setFieldValue("document", "");
                    }
                  }}
                />
              }
              label={t("Add New Category")}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectField
              size="small"
              options={updateDocumentOptions}
              label={t("Document") + `${!isAddingNewCategory ? "*" : ""}`}
              name="selectedContentType"
              formik={formik}
              disabled={isAddingNewCategory}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectField
              size="small"
              label={t("Category Type") + "*"}
              options={categoriesOptions || []}
              formik={formik}
              name="selectParentDocument"
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
            />
          </Grid>
          {!isAddingNewCategory && (
            <Grid item xs={12}>
              <InputFieldText
                label={t("Document Name") + "*"}
                placeholder="Enter Document Name"
                name="documentName"
                formik={formik}
              />
            </Grid>
          )}
          {isAddingNewCategory && (
            <Grid item xs={12}>
              <InputFieldText
                label={t("Category Name") + "*"}
                placeholder="Enter Category Name"
                name="categoryName"
                formik={formik}
              />
            </Grid>
          )}
          {!!selectedContentType && !isAddingNewDocument && (
            <Grid item xs={6} sm={4} md={3} lg={2} className="mt-3">
              <Box
                component="img"
                className="image"
                alt="document image"
                src={pdfImg}
                onClick={() => fileClickHandler()}
              />
              <Typography className="mt-1">{t("Current Document")}</Typography>
            </Grid>
          )}

          {!!document && (
            <Grid item xs={6} sm={4} md={3} lg={2} className="mt-3">
              <div className="image-container mb-0">
                <Box
                  component="img"
                  className="image"
                  alt={"document image"}
                  src={pdfImg}
                  onClick={() => newDocumentFileHandler()}
                />
                <i
                  className="ri-close-circle-line fs-3 close-btn"
                  onClick={() => setFieldValue("document", "")}
                />
              </div>
              <Typography className="mt-1">{t("Updated Document")}</Typography>
            </Grid>
          )}

          <Grid item sm={12} className="d-flex">
            {!document && !isAddingNewCategory && (
              <Grid item xs={12} className="mt-3">
                <input
                  accept="image/*, .pdf, .PDF"
                  type="file"
                  onChange={fileChangeHandler}
                />
              </Grid>
            )}
            <Grid item xs={12} className="text-end mt-3">
              <Button
                onClick={handleSubmit}
                color="primary"
                className="mb-1 me-1"
                variant="contained"
                disabled={isLoading}
              >
                {t("Save")} {isLoading && <CircularProgress size={20} />}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

DocumentModal.propTypes = {
  handleClose: PropTypes.func,
  isOpen: PropTypes.bool,
  menuData: PropTypes.array,
  t: PropTypes.func,
};

export default withTranslation()(DocumentModal);
